// client/src/pages/QuoteListPage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as quotesApi from '../api/quotes'; // Our frontend quotes API service
import LoadingSpinner from '../components/LoadingSpinner';

function QuoteListPage() {
    const { loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [filterStatus, setFilterStatus] = useState('all'); // Filter for quote status
    const [showArchived, setShowArchived] = useState(false); // Toggle to show archived quotes
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);

    // Calculate pagination
    const totalItems = quotes.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedQuotes = quotes.slice(startIndex, endIndex);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo(0, 0); // Scroll to top on page change
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1); // Reset to first page on items per page change
    };

    useEffect(() => {
        // Fetch quotes when component mounts or filters change
        const getQuotes = async () => {
            if (authLoading) {
                setLoading(false);
                return;
            }

            try {
                setError(null);
                setSuccess(null);
                setLoading(true);

                const filters = {};
                if (filterStatus !== 'all') {
                    filters.status = filterStatus;
                }
                filters.isArchived = showArchived; // Always send isArchived filter

                const response = await quotesApi.fetchQuotes(filters);
                // Ensure response.quotes is an array, default to empty array if undefined
                setQuotes(response.quotes || []); 
            } catch (err) {
                console.error('Failed to fetch quotes:', err);
                setError(err.message || 'Failed to load quotes.');
            } finally {
                setLoading(false);
            }
        };

        getQuotes();
    }, [authLoading, filterStatus, showArchived]); // Re-run when these dependencies change

    const handleDelete = async (quoteId) => {
        if (!window.confirm('Are you sure you want to permanently delete this quote? This action cannot be undone.')) {
            return;
        }
        setLoading(true);
        try {
            setError(null);
            setSuccess(null);
            await quotesApi.deleteQuote(quoteId);
            setSuccess('Quote deleted successfully!');
            // Remove from local state
            setQuotes(prevQuotes => prevQuotes.filter(quote => quote.id !== quoteId));
        } catch (err) {
            console.error('Failed to delete quote:', err);
            setError(err.message || 'Failed to delete quote.');
        } finally {
            setLoading(false);
        }
    };

    const handleArchive = async (quoteId) => {
        if (!window.confirm('Are you sure you want to archive this quote? It will no longer appear in your active list.')) {
            return;
        }
        setLoading(true);
        try {
            setError(null);
            setSuccess(null);
            await quotesApi.archiveQuote(quoteId);
            setSuccess('Quote archived successfully!');
            // Update local state to reflect change
            setQuotes(prevQuotes => prevQuotes.map(q => q.id === quoteId ? { ...q, isArchived: true, status: 'archived' } : q));
            // If not showing archived, filter it out from the view
            if (!showArchived) {
                setQuotes(prevQuotes => prevQuotes.filter(q => q.id !== quoteId));
            }
        } catch (err) {
            console.error('Failed to archive quote:', err);
            setError(err.message || 'Failed to archive quote.');
        } finally {
            setLoading(false);
        }
    };

    const handleUnarchive = async (quoteId) => {
        if (!window.confirm('Are you sure you want to unarchive this quote? It will reappear in your active list.')) {
            return;
        }
        setLoading(true);
        try {
            setError(null);
            setSuccess(null);
            await quotesApi.unarchiveQuote(quoteId);
            setSuccess('Quote unarchived successfully!');
            // Update local state to reflect change - you might need to refetch or adjust status as needed
            setQuotes(prevQuotes => prevQuotes.filter(q => q.id !== quoteId)); // Remove from list if viewing archived
        } catch (err) {
            console.error('Failed to unarchive quote:', err);
            setError(err.message || 'Failed to unarchive quote.');
        } finally {
            setLoading(false);
        }
    };

    const handleDuplicate = async (quoteId) => {
        if (!window.confirm('Are you sure you want to duplicate this quote?')) {
            return;
        }
        setLoading(true);
        try {
            setError(null);
            setSuccess(null);
            const response = await quotesApi.duplicateQuote(quoteId);
            setSuccess('Quote duplicated successfully!');
            navigate(`/quotes/edit/${response.quote.id}`); // Redirect to edit the new duplicated quote
        } catch (err) {
            console.error('Failed to duplicate quote:', err);
            setError(err.message || 'Failed to duplicate quote.');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'draft': return 'bg-gray-200 text-gray-800';
            case 'sent': return 'bg-blue-100 text-blue-800';
            case 'accepted': return 'bg-green-100 text-green-800';
            case 'rejected': return 'bg-red-100 text-red-800';
            case 'archived': return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    if (authLoading || loading) {
        return <LoadingSpinner message="Loading Quotes..." />;
    }

    if (error) {
        return (
            <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 p-4">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md max-w-md text-center" role="alert">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-[calc(100vh-64px)] bg-gray-50 p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto bg-white p-6 sm:p-8 rounded-xl shadow-lg border border-gray-200">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <h2 className="text-3xl font-bold text-primary-700">Your Quotes</h2>
                    <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                        <Link
                            to="/quotes/new"
                            className="px-4 py-2 bg-primary-600 text-white font-medium rounded-lg shadow-md hover:bg-primary-700 transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 text-center"
                        >
                            + Create New Quote
                        </Link>
                    </div>
                </div>
                <div className="mb-6"></div>

                {success && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md relative mb-4" role="alert">
                        <span className="block sm:inline">{success}</span>
                    </div>
                )}

                {/* Filters and Controls */}
                <div className="mb-6 p-4 bg-gray-100 rounded-lg flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <label htmlFor="filterStatus" className="text-sm font-medium text-gray-700">Status:</label>
                        <select
                            id="filterStatus"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="p-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm bg-white"
                        >
                            <option value="all">All</option>
                            <option value="draft">Draft</option>
                            <option value="sent">Sent</option>
                            <option value="accepted">Accepted</option>
                            <option value="rejected">Rejected</option>
                            <option value="archived">Archived</option>
                        </select>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="showArchived"
                            checked={showArchived}
                            onChange={(e) => setShowArchived(e.target.checked)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <label htmlFor="showArchived" className="text-sm font-medium text-gray-700">Show Archived</label>
                    </div>
                </div>

                {quotes.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                        <h3 className="text-xl font-semibold text-gray-700">No quotes yet</h3>
                        <p className="text-gray-500 mt-2 mb-4">Create your first quote to send to a client.</p>
                        <Link
                            to="/quotes/new"
                            className="px-6 py-3 bg-primary-600 text-white font-bold rounded-lg shadow-lg hover:bg-primary-700 transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 new-quote-button"
                        >
                            + Create Your First Quote
                        </Link>
                    </div>
                ) : (
                    <div>
                        {/* Desktop Table View (hidden on small screens) */}
                        <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-blue-50">
                                    <tr>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                                            Quote Name
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                                            Client
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-blue-700 uppercase tracking-wider">
                                            Total
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-blue-700 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-100">
                                    {paginatedQuotes.map((quote) => (
                                        <tr key={quote.id} className="hover:bg-gray-50 transition duration-150 ease-in-out">
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{quote.quoteName}</div>
                                                <div className="text-xs text-gray-500">Expires: {quote.expirationDate ? new Date(quote.expirationDate).toLocaleDateString() : 'Not Set'}</div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                {quote.clientName || 'Not Assigned'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(quote.status)}`}>
                                                    {quote.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                                                ${parseFloat(quote.grandTotal || 0).toFixed(2)}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end space-x-2">
                                                    {quote.publicAccessToken ? (
                                                        <Link
                                                            to={`/quotes/public/${quote.publicAccessToken}`}
                                                            className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50 transition duration-150 ease-in-out"
                                                            aria-label={`View ${quote.quoteName}`}
                                                            title="View Quote"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                                            </svg>
                                                        </Link>
                                                    ) : (
                                                        <button
                                                            onClick={() => alert('Public access token not available. Please edit and save the quote first.')}
                                                            className="text-gray-400 cursor-not-allowed p-1 rounded-full"
                                                            aria-label={`View ${quote.quoteName} (unavailable)`}
                                                            title="View Quote (unavailable)"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                                            </svg>
                                                        </button>
                                                    )}
                                                    <Link
                                                        to={`/quotes/edit/${quote.id}`}
                                                        className="text-primary-600 hover:text-primary-900 p-1 rounded-full hover:bg-primary-50 transition duration-150 ease-in-out"
                                                        aria-label={`Edit ${quote.quoteName}`}
                                                        title="Edit Quote"
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                                                    </Link>
                                                    <button
                                                        onClick={() => handleDuplicate(quote.id)}
                                                        className="text-gray-600 hover:text-gray-900 p-1 rounded-full hover:bg-gray-100 transition duration-150 ease-in-out"
                                                        aria-label={`Duplicate ${quote.quoteName}`}
                                                        title="Duplicate Quote"
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v4a2 2 0 002 2h4m-4 0h4m-4 0v4m0 0h6m-6 0h6M5 12h-.01M17 12h-.01M12 5h.01M12 17h.01M12 7V5m0 0H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                                                    </button>
                                                    {quote.isArchived ? (
                                                        <button
                                                            onClick={() => handleUnarchive(quote.id)}
                                                            className="text-yellow-600 hover:text-yellow-900 p-1 rounded-full hover:bg-yellow-50 transition duration-150 ease-in-out"
                                                            aria-label={`Unarchive ${quote.quoteName}`}
                                                            title="Unarchive Quote"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2m-4 0V3a1 1 0 00-1-1H9a1 1 0 00-1 1v1m-3 10l-3-3m0 0l-3 3m3-3h6m-3-4v6m0 0l-3-3m3 3l3-3"></path></svg>
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleArchive(quote.id)}
                                                            className="text-orange-600 hover:text-orange-900 p-1 rounded-full hover:bg-orange-50 transition duration-150 ease-in-out"
                                                            aria-label={`Archive ${quote.quoteName}`}
                                                            title="Archive Quote"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"></path></svg>
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleDelete(quote.id)}
                                                        className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition duration-150 ease-in-out"
                                                        aria-label={`Delete ${quote.quoteName}`}
                                                        title="Delete Quote"
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View (visible on small screens) */}
                        <div className="md:hidden space-y-4">
                            {paginatedQuotes.map((quote) => (
                                <div key={quote.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm relative">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900">{quote.quoteName}</h3>
                                            <p className="text-xs text-gray-500">Expires: {quote.expirationDate ? new Date(quote.expirationDate).toLocaleDateString() : 'Not Set'}</p>
                                        </div>
                                        <div className="flex-shrink-0">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(quote.status)}`}>
                                                {quote.status}
                                            </span>
                                        </div>
                                    </div>
                                    <p className="text-sm text-gray-600 mb-1">
                                        <span className="font-medium">Client:</span> {quote.clientName || 'Not Assigned'}
                                    </p>
                                    <p className="text-sm font-semibold text-gray-900 mb-3">
                                        <span className="font-medium text-gray-600">Total:</span> ${parseFloat(quote.grandTotal || 0).toFixed(2)}
                                    </p>

                                    <div className="flex justify-end space-x-2 border-t border-gray-100 pt-3">
                                        {quote.publicAccessToken ? (
                                            <Link
                                                to={`/quotes/public/${quote.publicAccessToken}`}
                                                className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50 transition duration-150 ease-in-out"
                                                title="View Quote"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                                </svg>
                                            </Link>
                                        ) : (
                                            <button
                                                onClick={() => alert('Public access token not available. Please edit and save the quote first.')}
                                                className="text-gray-400 cursor-not-allowed p-1 rounded-full"
                                                title="View Quote (unavailable)"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                                </svg>
                                            </button>
                                        )}
                                        <Link
                                            to={`/quotes/edit/${quote.id}`}
                                            className="text-primary-600 hover:text-primary-900 p-1 rounded-full hover:bg-primary-50 transition duration-150 ease-in-out"
                                            aria-label={`Edit ${quote.quoteName}`}
                                            title="Edit Quote"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                                        </Link>
                                        <button
                                            onClick={() => handleDuplicate(quote.id)}
                                            className="text-gray-600 hover:text-gray-900 p-1 rounded-full hover:bg-gray-100 transition duration-150 ease-in-out"
                                            aria-label={`Duplicate ${quote.quoteName}`}
                                            title="Duplicate Quote"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v4a2 2 0 002 2h4m-4 0h4m-4 0v4m0 0h6m-6 0h6M5 12h-.01M17 12h-.01M12 5h.01M12 17h.01M12 7V5m0 0H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                                        </button>
                                        {quote.isArchived ? (
                                            <button
                                                onClick={() => handleUnarchive(quote.id)}
                                                className="text-yellow-600 hover:text-yellow-900 p-1 rounded-full hover:bg-yellow-50 transition duration-150 ease-in-out"
                                                aria-label={`Unarchive ${quote.quoteName}`}
                                                title="Unarchive Quote"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2m-4 0V3a1 1 0 00-1-1H9a1 1 0 00-1 1v1m-3 10l-3-3m0 0l-3 3m3-3h6m-3-4v6m0 0l-3-3m3 3l3-3"></path></svg>
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleArchive(quote.id)}
                                                className="text-orange-600 hover:text-orange-900 p-1 rounded-full hover:bg-orange-50 transition duration-150 ease-in-out"
                                                aria-label={`Archive ${quote.quoteName}`}
                                                title="Archive Quote"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"></path></svg>
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleDelete(quote.id)}
                                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition duration-150 ease-in-out"
                                            aria-label={`Delete ${quote.quoteName}`}
                                            title="Delete Quote"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {quotes.length > 0 && (
                    <div className="mt-6 flex flex-col items-center space-y-4 sm:flex-row sm:justify-between sm:space-y-0">
                        <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600">Items per page:</span>
                            <select 
                                value={itemsPerPage} 
                                onChange={handleItemsPerPageChange}
                                className="p-2 border border-gray-300 rounded-md text-sm"
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                            >
                                Previous
                            </button>
                            <span className="text-sm text-gray-600">
                                Page {currentPage} of {totalPages} ({startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} items)
                            </span>
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default QuoteListPage;
